import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScanLine, ArrowRight, Bell, Receipt, Utensils, HelpCircle } from "lucide-react";

export default function Landing() {
  const [code, setCode] = useState("");
  const nav = useNavigate();

  const openTable = (e) => {
    e.preventDefault();
    const c = code.trim();
    if (c) nav(`/t/${c}`);
  };

  return (
    <div className="customer-scope min-h-screen">
      <div className="max-w-5xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-24">
        <div className="overline text-[#c85a40]">Welcome to your table</div>
        <h1 className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight mt-5 text-[#1a1c19]">
          Everything you need, <br />
          <em className="text-[#c85a40] not-italic">one tap away.</em>
        </h1>
        <p className="font-plex text-base md:text-lg text-[#5c605a] max-w-xl mt-6 leading-relaxed">
          See the menu, call a waiter, ask for the check or place an order —
          all from your phone, without waving anyone down.
        </p>

        <div className="mt-14 grid md:grid-cols-2 gap-8 items-start">
          {/* Left: scan + code input */}
          <div className="bg-[#f2efea] border border-[#e5e1d8] p-8 md:p-10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 border border-[#e5e1d8] bg-[#f9f8f6] inline-flex items-center justify-center">
                <ScanLine size={18} className="text-[#c85a40]" />
              </div>
              <div className="overline text-[#c85a40]">Start here</div>
            </div>
            <h2 className="font-serif-display text-3xl mt-6 tracking-tight">Scan the QR at your table</h2>
            <p className="font-plex text-sm text-[#5c605a] mt-3 leading-relaxed">
              Point your phone camera at the QR sticker on your table and tap the link.
            </p>

            <form onSubmit={openTable} className="mt-8 border-t border-[#e5e1d8] pt-6">
              <label className="overline text-[#5c605a]">Or enter your table code</label>
              <div className="mt-3 flex gap-2">
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="e.g. QEMp3pem"
                  data-testid="guest-code-input"
                  className="flex-1 bg-[#f9f8f6] border border-[#e5e1d8] px-3 py-2.5 font-plex text-sm focus:outline-none focus:border-[#1a1c19]"
                />
                <button
                  type="submit"
                  data-testid="guest-open-btn"
                  className="bg-[#1a1c19] text-[#f9f8f6] px-4 py-2.5 font-plex text-sm hover:bg-[#c85a40] transition-colors flex items-center gap-2"
                >
                  Open <ArrowRight size={14} />
                </button>
              </div>
              <p className="font-plex text-xs text-[#5c605a] mt-3">The code is printed on the QR sticker on your table.</p>
            </form>
          </div>

          {/* Right: what you can do */}
          <div className="pt-4">
            <div className="overline text-[#5c605a]">What you can do</div>
            <ul className="mt-6 space-y-6">
              {[
                { icon: Utensils, t: "Browse the menu", d: "Categories, prices and descriptions — always up to date." },
                { icon: Bell, t: "Call a waiter", d: "One tap alerts your server that you need them at the table." },
                { icon: Receipt, t: "Ask for the check", d: "Ready to leave? Request the bill and it'll be brought over." },
                { icon: HelpCircle, t: "Ask for help", d: "Allergy question, missing utensil, extra napkins — just ask." },
              ].map((f) => (
                <li key={f.t} className="flex gap-4">
                  <div className="w-10 h-10 border border-[#e5e1d8] bg-[#f2efea] inline-flex items-center justify-center shrink-0">
                    <f.icon size={16} className="text-[#c85a40]" />
                  </div>
                  <div>
                    <div className="font-serif-display text-xl leading-tight">{f.t}</div>
                    <div className="font-plex text-sm text-[#5c605a] mt-1 leading-relaxed">{f.d}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
